import { useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { createStandaloneWorkOrder } from '../../services/workOrdersTabService';

interface ProjectOption {
  ProjectKey: string;
  ProjectName: string;
  ProjectId: string;
}

interface NewWorkOrderDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (workOrder: { WorkOrderKey: string; WorkOrderId: string; ProjectId: string }) => void;
  projects: ProjectOption[];
}

const todayStr = () => new Date().toISOString().split('T')[0];

export const NewWorkOrderDialog = ({
  open,
  onClose,
  onCreated,
  projects,
}: NewWorkOrderDialogProps) => {
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [workOrderName, setWorkOrderName] = useState('');
  const [startDate, setStartDate] = useState(todayStr());
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = () => {
    setSelectedProject(null);
    setWorkOrderName('');
    setStartDate(todayStr());
    setRemarks('');
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!selectedProject) errs.project = 'Project is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const result = await createStandaloneWorkOrder({
        projectKey: selectedProject!.ProjectKey,
        workOrderName: workOrderName.trim() || undefined,
        workOrderStartDate: startDate || undefined,
        remarks: remarks.trim() || undefined,
      });
      resetForm();
      onCreated(result);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setErrors({ submit: msg || 'Failed to create work order. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>New Work Order</DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
        {/* Project */}
        <Autocomplete
          size="small"
          options={projects}
          getOptionLabel={(opt) => `${opt.ProjectId} - ${opt.ProjectName}`}
          value={selectedProject}
          onChange={(_e, val) => setSelectedProject(val)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project"
              required
              placeholder="Select a project..."
              error={!!errors.project}
              helperText={errors.project}
            />
          )}
        />

        {/* Work Order Name */}
        <TextField
          size="small"
          label="Work Order Name"
          placeholder="Optional descriptive name..."
          value={workOrderName}
          onChange={(e) => setWorkOrderName(e.target.value)}
        />

        {/* Start Date */}
        <TextField
          size="small"
          type="date"
          label="Start Date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        {/* Remarks */}
        <TextField
          size="small"
          label="Remarks"
          multiline
          rows={3}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />

        {errors.submit && (
          <Typography variant="body2" color="error">{errors.submit}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Create Work Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};
